import { useEffect, useState } from "react";
import { SpinnerBlock } from "../../Utils/CustomComponent";
import { useDispatch, useSelector } from "react-redux";
import { fetchPortfolioAtGlance } from "../../store/reducers/dashboardSlice/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import CommonGrid from "../CommonGrid";
import { gridColumns_PortfolioBalancePie } from "../../Utils/TableColumnDefs";
import NumberFormat from "react-number-format";
import formatNumber from "format-number";
import { reduceSum } from "../../Utils";
import { Badge } from "react-bootstrap";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const PorfolioAtGlanceIndependent = () => {
  const dispatch = useDispatch();

  const portfolioAtGlanceStatus = useSelector(
    (state) => state.dashboard.portfolioAtGlance.status
  );
  const portfolioAtGlance = useSelector(
    (state) => state.dashboard.portfolioAtGlance.data
  );
  const lenderId = useSelector((state) => state.account.lenderId);

  useEffect(() => {
    if (portfolioAtGlanceStatus === "idle") {
      dispatch(fetchPortfolioAtGlance({ lenderId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, lenderId, portfolioAtGlanceStatus]);

  const datasets = portfolioAtGlance?.map((data) => data.principal);

  return portfolioAtGlanceStatus === "fulfilled" ? (
    <>
      <LoanAmountPieChart
        loanAmounts={datasets}
        loanDetails={portfolioAtGlance}
      />
    </>
  ) : (
    <SpinnerBlock height={"70vh"} />
  );
};

export default PorfolioAtGlanceIndependent;

const LoanAmountPieChart = ({ loanAmounts, loanDetails }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loanAmountTotal, setLoanAmountTotal] = useState(null);
  const [refinedLoanDetails, setRefinedLoanDetails] = useState([]);
  useEffect(() => {
    const sum = loanAmounts.reduce((acc, curr) => acc + curr, 0);
    setLoanAmountTotal(sum);
  }, [loanAmounts]);
  useEffect(() => {
    const getFilteredLoans = (range) => {
      return loanDetails
        .filter(
          (loan) =>
            loan.principal >= range.min &&
            (range.max === undefined || loan.principal <= range.max)
        )
        .map((loan) => ({
          loanAccount: loan.account,
          loan_id: loan.loan_id,
          amount: loan.principal,
        }));
    };
    const firstRange = ranges[0];
    const loansInFirstRange = getFilteredLoans(firstRange);

    const refined = loanDetails?.map((loan) => ({
      loanAccount: loan.account,
      loan_id: loan.loan_id,
      amount: loan.principal,
    }));
    setRefinedLoanDetails(refined);

    setSelectedCategory({
      label: "total",
      loans: loanDetails,
      count: loanDetails?.length,
      totalAmount: formatNumber({ prefix: "$", round: 2 })(
        parseFloat(
          reduceSum(loanDetails?.map((data) => data.principal))
        ).toFixed(5)
      ),
    });
  }, [loanDetails]);
  const counts = ranges.map(() => 0);

  loanAmounts?.forEach((amount) => {
    const rangeIndex = ranges.findIndex(
      (range) =>
        amount >= range.min && (range.max === undefined || amount <= range.max)
    );
    if (rangeIndex !== -1) {
      counts[rangeIndex]++;
    }
  });

  const pieData = {
    labels: ranges.map((range) => range.label),
    datasets: [
      {
        data: counts,
        // radius: "90%",
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
        ],
        // hoverBackgroundColor: [
        //   "#CC516A", // darkened red
        //   "#2A8CD6", // darkened blue
        //   "#E6B84B", // darker yellow
        //   "#3BAEAE", // darker teal
        //   "#7A4DD9", // darker purple
        // ],
        hoverBorderColor: "#333",
        hoverBorderWidth: 1,
        borderWidth: ranges.map((range) =>
          range.label === selectedCategory?.label ? 1 : 0
        ),
        borderColor: ranges.map((range) =>
          range.label === selectedCategory?.label ? "#333" : ""
        ),
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    // maintainAspectRatio: false,
    // indexAxis: "y",
    plugins: {
      legend: {
        position: "right",
        onClick: () => {},
        // display: false,
        labels: {
          boxWidth: 50, // Increases the size of the color box (tile)
          boxHeight: 20, // Optional: for more control, available in newer versions
          color: 'black',  
          font: {
            size: 16, // Increases legend label font size
            weight: 'bold',
          },
        },
      },
      tooltip: {
        titleFont: {
          size: 16, // Adjust title font size
        },
        bodyFont: {
          size: 16, // Adjust body font size
        },
      },
    },
    // scales: {
    //   x: {
    //     reverse: true, // ⬅️ Bar grows to the left
    //     beginAtZero: true,
    //     ticks: {
    //       precision: 0,
    //     },
    //   },
    //   y: {
    //     position: "right", // ✅ Labels appear on the right
    //     ticks: {
    //       align: "start",
    //     },
    //   },
    // },
    onHover: (event, chartElement) => {
      const target = event.native ? event.native.target : event.target;
      if (chartElement.length) {
        target.style.cursor = "pointer";
      } else {
        target.style.cursor = "default";
      }
    },
    onClick: (event, elements) => {
      if (elements?.length) {
        const categoryIndex = elements[0].index;
        const selectedRange = ranges[categoryIndex];

        const filteredLoans = loanDetails.filter(
          (loan) =>
            loan.principal > selectedRange.min &&
            (selectedRange.max === undefined ||
              loan.principal <= selectedRange.max)
        );
        const selectedLoans = filteredLoans.map((loan) => {
          return {
            loanAccount: loan.account,
            loan_id: loan.loan_id,
            amount: loan.principal,
          };
        });

        setSelectedCategory({
          label: selectedRange?.label,
          loans: selectedLoans,
          count: selectedLoans?.length,
          totalAmount: formatNumber({ prefix: "$", round: 2 })(
            parseFloat(
              reduceSum(selectedLoans?.map((data) => data.amount))
            ).toFixed(5)
          ),
        });
      }
    },
  };

  return (
    <div className="d-flex">
      <div
        className="d-flex align-items-center flex-column mt-3"
        style={{ height: "100%", width: "70vh" }}
      >
        <p style={{ marginBottom: "-20px" }}>
          <span className="fw-bold custom-title">
            Total Portfolio Balance:&nbsp;
          </span>
          <NumberFormat
            displayType="text"
            className="custom-title"
            value={loanAmountTotal}
            prefix={"$"}
            customInput={false}
            fixedDecimalScale={true}
            decimalScale={2}
            thousandSeparator={true}
          />
        </p>

        <div className="mt-5 pt-5">
          <Badge bg="info" pill className="ms-auto p-5 fs-5">
            📊 Click on a bar to view associated loan accounts.
          </Badge>
        </div>
        {/* <div className=" p-2 me-3 mt-5" style={{ height: "100%" }}> */}
        <Pie data={pieData} options={pieOptions} />
        {/* </div> */}
      </div>
      {selectedCategory && (
        <div className="p-4" style={{ background: "#eeeeee" }}>
          <div className="mx-auto">
            <CommonGrid
              className={"loans_in_workout bg-white col-sm-12"}
              title={
                selectedCategory.label === "total"
                  ? `Loan Portfolio: ${selectedCategory.count} Accounts with a Total Amount of ${selectedCategory?.totalAmount} across All Ranges`
                  : `Loan Portfolio: ${selectedCategory.count} Accounts with a Total Amount of ${selectedCategory?.totalAmount} in the ${selectedCategory.label} Range`
              }
              gridColumns={gridColumns_PortfolioBalancePie}
              columnData={
                selectedCategory.label === "total"
                  ? refinedLoanDetails
                  : selectedCategory.loans
              }
              tableTotal={["amount"]}
            />
          </div>
        </div>
      )}
    </div>
  );
};
const ranges = [
  { min: 0, max: 250000, label: "$0 - $250K" },
  { min: 250000, max: 500000, label: "$250K+ - $500K" },
  { min: 500000, max: 750000, label: "$500K+ - $750K" },
  { min: 750000, max: 1000000, label: "$750K+ - $1M" },
  { min: 1000000, label: "$1M+" },
];
